import { cva } from "class-variance-authority";

import { RegrelloIntent } from "../../tokens/enums/regrelloIntent";
import { RegrelloSize } from "../../tokens/enums/regrelloSize";

export const chipVariants = cva<{
  intent: Record<RegrelloIntent, string>;
  size: Record<Extract<RegrelloSize, "medium" | "small" | "x-small">, string>;
  isClickable: {
    true: string;
    false: string;
  };
  isDisabled: {
    true: string;
    false: string;
  };
  isDeletable: {
    true: string;
    false: string;
  };
  isSelected: {
    true: string;
    false: string;
  };
  variant: {
    soft: string;
    surface: string;
  };
}>(
  `
  inline-flex
  items-center
  gap-1

  w-auto
  min-w-0

  rounded
  box-inset

  cursor-default
  `,
  {
    variants: {
      intent: {
        neutral: "",
        primary: "",
        special: "",
        success: "",
        warning: "",
        danger: "",
      },
      size: {
        [RegrelloSize.MEDIUM]: "px-2 py-1 text-sm",
        [RegrelloSize.SMALL]: "px-1.5 py-0.5 text-xs",
        [RegrelloSize.X_SMALL]: "px-1 py-0 text-xs",
      },
      isClickable: {
        true: `
          cursor-pointer

          focus-visible:outline-3
          focus-visible:-outline-offset-1
          focus-visible:outline-primary-solid
        `,
        false: "",
      },
      isDeletable: {
        true: "pr-6",
        false: "",
      },
      isDisabled: {
        true: "opacity-50 cursor-not-allowed",
        false: "",
      },
      isSelected: {
        true: "", // (clewis): Will only be applicable if isDisabled is false.
        false: "",
      },
      variant: {
        soft: "",
        surface: "",
      },
    },
    compoundVariants: [
      // Selected
      {
        isDisabled: false,
        isSelected: true,
        class:
          // (clewis): Make it appear focused without actually being focused.
          "outline-solid outline-2 outline-offset--2 outline-primary-solid focus-visible:outline-width-2 focus-visible:outline-offset--2",
      },

      // Surface variant
      {
        intent: "neutral",
        variant: "surface",
        class: "shadow-neutral-borderInteractive bg-neutral-background text-neutral-text",
      },
      {
        intent: "primary",
        variant: "surface",
        class: "shadow-primary-borderInteractive bg-primary-background text-primary-text",
      },
      {
        intent: "special",
        variant: "surface",
        class: "shadow-special-borderInteractive bg-special-background text-special-text",
      },
      {
        intent: "success",
        variant: "surface",
        class: "shadow-success-borderInteractive bg-success-background text-success-text",
      },
      {
        intent: "warning",
        variant: "surface",
        class: "shadow-warning-borderInteractive bg-warning-background text-warning-text",
      },
      {
        intent: "danger",
        variant: "surface",
        class: "shadow-danger-borderInteractive bg-danger-background text-danger-text",
      },
      // (Clickable)
      {
        isClickable: true,
        isDisabled: false,
        intent: "neutral",
        variant: "surface",
        class:
          "hover:shadow-neutral-borderInteractiveHovered hover:bg-neutral-softHovered active:bg-neutral-softPressed",
      },
      {
        isClickable: true,
        isDisabled: false,
        intent: "primary",
        variant: "surface",
        class:
          "hover:shadow-primary-borderInteractiveHovered hover:bg-primary-softHovered active:bg-primary-softPressed",
      },
      {
        isClickable: true,
        isDisabled: false,
        intent: "special",
        variant: "surface",
        class:
          "hover:shadow-special-borderInteractiveHovered hover:bg-special-softHovered active:bg-special-softPressed",
      },
      {
        isClickable: true,
        isDisabled: false,
        intent: "success",
        variant: "surface",
        class:
          "hover:shadow-success-borderInteractiveHovered hover:bg-success-softHovered active:bg-success-softPressed",
      },
      {
        isClickable: true,
        isDisabled: false,
        intent: "warning",
        variant: "surface",
        class:
          "hover:shadow-warning-borderInteractiveHovered hover:bg-warning-softHovered active:bg-warning-softPressed",
      },
      {
        isClickable: true,
        isDisabled: false,
        intent: "danger",
        variant: "surface",
        class: "hover:shadow-danger-borderInteractiveHovered hover:bg-danger-softHovered active:bg-danger-softPressed",
      },

      // Soft variant
      {
        intent: "neutral",
        variant: "soft",
        class: "bg-neutral-soft text-neutral-text",
      },
      {
        intent: "primary",
        variant: "soft",
        class: "bg-primary-soft text-primary-text",
      },
      {
        intent: "special",
        variant: "soft",
        class: "bg-special-soft text-special-text",
      },
      {
        intent: "success",
        variant: "soft",
        class: "bg-success-soft text-success-text",
      },
      {
        intent: "warning",
        variant: "soft",
        class: "bg-warning-soft text-warning-text",
      },
      {
        intent: "danger",
        variant: "soft",
        class: "bg-danger-soft text-danger-text",
      },
      // (Clickable)
      {
        isClickable: true,
        isDisabled: false,
        intent: "neutral",
        variant: "soft",
        class: "hover:bg-neutral-softHovered active:bg-neutral-softPressed",
      },
      {
        isClickable: true,
        isDisabled: false,
        intent: "primary",
        variant: "soft",
        class: "hover:bg-primary-softHovered active:bg-primary-softPressed",
      },
      {
        isClickable: true,
        isDisabled: false,
        intent: "special",
        variant: "soft",
        class: "hover:bg-special-softHovered active:bg-special-softPressed",
      },
      {
        isClickable: true,
        isDisabled: false,
        intent: "success",
        variant: "soft",
        class: "hover:bg-success-softHovered active:bg-success-softPressed",
      },
      {
        isClickable: true,
        isDisabled: false,
        intent: "warning",
        variant: "soft",
        class: "hover:bg-warning-softHovered active:bg-warning-softPressed",
      },
      {
        isClickable: true,
        isDisabled: false,
        intent: "danger",
        variant: "soft",
        class: "hover:bg-danger-softHovered active:bg-danger-softPressed",
      },

      {
        isDeletable: true,
        isDisabled: false,
        size: "x-small",
        class: "pr-5.5",
      },
      {
        isDeletable: true,
        isDisabled: false,
        size: "small",
        class: "pr-5.5",
      },
    ],
    defaultVariants: {
      intent: "neutral",
      isClickable: false,
      isSelected: false,
      size: RegrelloSize.MEDIUM,
    },
  },
);

export const chipDeleteButtonVariants = cva<{
  intent: Record<RegrelloIntent, string>;
  size: Record<Extract<RegrelloSize, "medium" | "small" | "x-small">, string>;
}>(
  `
  rounded-full

  absolute
  top-1/2
  translate-y--1/2
  `,
  {
    variants: {
      intent: {
        neutral: "text-textDefault hover:bg-neutral-softHovered active:bg-neutral-softPressed",
        primary: "text-primary-textMuted hover:bg-primary-softHovered active:bg-primary-softPressed",
        special: "text-special-textMuted hover:bg-primary-softHovered active:bg-primary-softPressed",
        success: "text-success-textMuted hover:bg-success-softHovered active:bg-success-softPressed",
        warning: "text-warning-textMuted hover:bg-warning-softHovered active:bg-warning-softPressed",
        danger: "text-danger-textMuted hover:bg-danger-softHovered active:bg-danger-softPressed",
      },
      size: {
        "x-small": "right-1",
        small: "right-1",
        medium: "right-1.5",
      },
    },
    defaultVariants: {
      intent: "neutral",
    },
  },
);

export function getChipIconColorCssClass(intent: RegrelloIntent): string {
  /* eslint-disable lingui/no-unlocalized-strings */
  switch (intent) {
    case RegrelloIntent.PRIMARY:
      return "text-primary-icon";
    case RegrelloIntent.SPECIAL:
      return "text-special-icon";
    case RegrelloIntent.SUCCESS:
      return "text-onTrack-icon";
    case RegrelloIntent.WARNING:
      return "text-warning-icon";
    case RegrelloIntent.DANGER:
      return "text-danger-icon";
    default:
      return "text-neutral-icon";
  }
  /* eslint-enable lingui/no-unlocalized-strings */
}
